import type { PaginationOrder, PaginationSpec } from "@rpcbase/api"

import type { PaginationCursorCodecOptions } from "./cursor"
import { decodePaginationCursor } from "./cursor"
import { PaginationValidationError } from "./errors"
import type { NormalizedPaginationSpec } from "./normalizeSpec"
import { normalizePaginationSpec } from "./normalizeSpec"


export type CompiledMongoPagination = {
  spec: NormalizedPaginationSpec;
  mongoFilterDelta: Record<string, unknown>;
  mongoSort: Record<string, 1 | -1>;
  mongoLimit: number;
}

export type CompileMongoPaginationOptions = {
  cursor: PaginationCursorCodecOptions;
}

export const compileMongoPagination = (
  spec: PaginationSpec,
  options: CompileMongoPaginationOptions,
): CompiledMongoPagination => {
  const normalized = normalizePaginationSpec(spec)

  const mongoLimit = normalized.limit + 1
  const mongoSort = toMongoSort(normalized)

  if (normalized.cursor == null) {
    return {
      spec: normalized,
      mongoFilterDelta: {},
      mongoSort,
      mongoLimit,
    }
  }

  if (typeof normalized.cursor !== "string" || normalized.cursor.length === 0) {
    throw new PaginationValidationError("Invalid pagination cursor")
  }

  const cursorValues = decodePaginationCursor(normalized, normalized.cursor, options.cursor)
  const mongoFilterDelta = buildKeysetFilterDelta(normalized, cursorValues)

  return {
    spec: normalized,
    mongoFilterDelta,
    mongoSort,
    mongoLimit,
  }
}

const toMongoSort = (spec: NormalizedPaginationSpec): Record<string, 1 | -1> => {
  const mongoSort = Object.create(null) as Record<string, 1 | -1>
  for (const { field, order } of spec.sort) {
    const forQueryOrder = spec.direction === "prev" ? invertOrder(order) : order
    mongoSort[field] = forQueryOrder === "asc" ? 1 : -1
  }
  return mongoSort
}

const buildKeysetFilterDelta = (
  spec: NormalizedPaginationSpec,
  cursorValues: Record<string, unknown>,
): Record<string, unknown> => {
  const branches: Record<string, unknown>[] = []

  for (let i = 0; i < spec.sort.length; i++) {
    const current = spec.sort[i]
    if (!current) continue

    const and: Record<string, unknown>[] = []

    for (let j = 0; j < i; j++) {
      const prev = spec.sort[j]
      if (!prev) continue
      const eq = Object.create(null) as Record<string, unknown>
      eq[prev.field] = cursorValues[prev.field]
      and.push(eq)
    }

    const op = getKeysetOp(current.order, spec.direction)
    const cmpOp = Object.create(null) as Record<string, unknown>
    cmpOp[op] = cursorValues[current.field]
    const cmp = Object.create(null) as Record<string, unknown>
    cmp[current.field] = cmpOp
    and.push(cmp)

    branches.push(and.length === 1 ? and[0]! : { $and: and })
  }

  return { $or: branches }
}

const getKeysetOp = (order: PaginationOrder, direction: NormalizedPaginationSpec["direction"]): "$gt" | "$lt" => {
  if (direction === "next") return order === "asc" ? "$gt" : "$lt"
  return order === "asc" ? "$lt" : "$gt"
}

const invertOrder = (order: PaginationOrder): PaginationOrder => {
  return order === "asc" ? "desc" : "asc"
}
